// auth.js - Authentication and session management.

import { CONFIG } from './config.js';

/**
 * Is this evaluation row one that `me` wrote? Pure, so it can be tested
 * without a session; AUTH.ownsEvaluation() supplies the live identity.
 *
 * A staff coach signs in with a personal ST- code, but their evaluations are
 * stored against `staff:<id>` in team_code and their id in author_staff_id.
 * Comparing the login code to team_code therefore never matched, so the player
 * list showed "not rated" for players they had just evaluated, and tapping one
 * started a SECOND evaluation instead of opening the first. Both the list and
 * the tap handler go through here so they cannot disagree again.
 *
 * @param {object} rec  evaluation row: { team_code, author_staff_id }
 * @param {object} me   { staffId, code }
 */
export function isOwnEvaluation(rec, me = {}) {
  if (!rec) return false;
  if (me.staffId) return rec.author_staff_id === me.staffId;
  return !!me.code && rec.team_code === me.code;
}

/**
 * Does this token carry a club?
 *
 * Every login now stamps club_id into the token and the row-level policies
 * read it, so a token without one is a session from before that change: the
 * database will show it nothing. Reads the CLAIM rather than the session
 * object, so one check covers every login type whatever shape its session has.
 *
 * The signature is deliberately not verified here. The server does that on
 * every request, and the only thing this decides is whether to throw a stale
 * session AWAY, so being wrong costs a re-login and never grants anything.
 * Pure, so the thing that can log everybody out is tested.
 *
 * @param {string} token  a signed session JWT
 */
export function tokenHasClub(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return false;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const club = JSON.parse(json).club_id;
    return typeof club === 'string' && club.trim().length > 0;
  } catch {
    return false;
  }
}

export const AUTH = (() => {

  // ── SESSION STORAGE KEY ──────────────────────────────────────────────────────
  const SESSION_KEY = 'khc_dragons_session';

  // ── SESSION STRUCTURE ────────────────────────────────────────────────────────
  // {
  //   mode: 'director' | 'coach' | 'feedback',
  //   coachName: string,
  //   code: string,
  //   codeType: 'director' | 'full' | 'feedback',
  //   team: { id, age_group } | null,
  //   pillars: string[] | null,   // null = all pillars
  //   playerId: string | null,    // set for individual player codes
  //   expiresAt: string | null,
  // }

  let _session = null;

  // ── INITIALISE ───────────────────────────────────────────────────────────────

  // Load any existing session from sessionStorage.
  function init() {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) _session = JSON.parse(stored);
      // Every login now stamps the club into the token, and the row-level
      // policies read it. A session opened BEFORE that change has no club, so
      // the database would show it nothing: an empty screen with no
      // explanation. Drop it instead and let the gate ask for the code again,
      // which is the one failure a person can actually act on.
      if (_session && !tokenHasClub(_session.token)) {
        _session = null;
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      _session = null;
    }
  }

  // ── CODE VALIDATION ──────────────────────────────────────────────────────────

  // Validate a code and return the result.
  async function validateCode(code) {
    // Player access codes carry the PA- prefix and live in their own Edge
    // Function (player-auth) - route them there; everything else goes to
    // exchange-code as before.
    const trimmed = code.trim();
    if (trimmed.toUpperCase().startsWith(CONFIG.player.codePrefix)) {
      const r = await _callPlayerAuth({ code: trimmed });
      if (!r.ok) return { valid: false, reason: r.error };
      _session = { ...r.session, token: r.token };
      _persist();
      return { valid: true, isPlayer: true };
    }

    // Staff codes (Module 6) carry the ST- prefix -> staff-auth function.
    if (trimmed.toUpperCase().startsWith(CONFIG.staffHub.codePrefix)) {
      const r = await _callFunction('staff-auth', { code: trimmed });
      if (!r.ok) return { valid: false, reason: r.error };
      _session = { ...r.session, token: r.token };
      _persist();
      return { valid: true, isStaff: true };
    }

    // Squad codes: one shared code for a team, who then say who they are.
    // Checked before the generic exchange so it never reaches the coach flow.
    //
    // An unrecognised code FALLS THROUGH to the generic exchange rather than
    // being rejected here. The prefix is a hint about where to look first, not
    // a verdict: 'VIC' is every code Royal Victory will ever have, so routing
    // on it alone meant their director code was sent to the squad function and
    // the gate said "invalid code" about a code that was perfectly valid. A
    // prefix guess should never be able to deny a real login.
    if ((CONFIG.squad?.codePrefixes || []).some(p => trimmed.toUpperCase().startsWith(p))) {
      const r = await _callFunction('squad-auth', { code: trimmed });
      if (r.ok) {
        _session = { ...r.session, token: r.token };
        _persist();
        return { valid: true, isSquad: true };
      }
      // Anything other than "not one of mine" is a real answer about a real
      // squad code (revoked, for instance) and is reported as such.
      if (r.error !== 'invalid_code') return { valid: false, reason: r.error };
    }

    // Developer codes (DEV-) reach a club for support, and every entry is
    // logged server-side before a token exists. Two steps: the first call
    // returns the clubs and mints nothing, so picking one is a deliberate act
    // rather than a side effect of typing a code. The caller drives step two
    // through enterClubAsDeveloper().
    //
    // Like the squad prefix above, a miss FALLS THROUGH rather than denying:
    // a prefix is a hint about where to look first, never a verdict.
    if (trimmed.toUpperCase().startsWith('DEV-')) {
      const r = await _callFunction('developer-auth', { code: trimmed });
      if (r.ok && r.developer) {
        _pendingDeveloper = { code: trimmed, label: r.label };
        return { valid: true, isDeveloper: true, label: r.label, clubs: r.clubs || [] };
      }
      if (r.ok === false && r.error !== 'invalid_code') {
        return { valid: false, reason: r.error };
      }
    }

    // Baseline tester codes (Module 2) carry the BT- prefix.
    if (trimmed.toUpperCase().startsWith(CONFIG.baseline.codePrefix)) {
      const r = await _callFunction('baseline-tester-auth', { code: trimmed });
      if (!r.ok) return { valid: false, reason: r.error };
      _session = { ...r.session, token: r.token };
      _persist();
      return { valid: true, isBaselineTester: true };
    }

    // First call without a name. Director / selection branches return a token
    // immediately; coach branch returns needsName + codeType only.
    const r = await _exchange(trimmed);
    if (!r.ok) return { valid: false, reason: r.error };
    if (r.session?.mode === 'director') {
      _pendingExchange = r;             // remember to consume in setDirectorSession
      return { valid: true, isDirector: true };
    }
    if (r.session?.mode === 'codirector') {
      // Limited second director - pre-named, no name screen; adopt immediately.
      _session = { ...r.session, token: r.token };
      _persist();
      return { valid: true, isCoDirector: true };
    }
    if (r.session?.mode === 'selection') {
      // Selection coach codes are pre-named (trainer name lives server-side).
      // No name screen - just adopt the session immediately.
      _session = { ...r.session, token: r.token };
      _persist();
      return { valid: true, isSelection: true };
    }
    if (r.needsName) {
      _pendingCode = code.trim();       // remember for the post-name exchange
      return { valid: true, isDirector: false, record: { type: r.codeType } };
    }
    return { valid: false, reason: 'unexpected' };
  }

  // ── INTERNAL - Edge Function call ────────────────────────────────────────────

  let _pendingCode = null;
  let _pendingExchange = null;

  async function _exchange(code, name) {
    return _callFunction('exchange-code', { code, name });
  }

  // player-auth (Module 1): code login AND anonymous landing-page submissions.
  function _callPlayerAuth(payload) {
    return _callFunction('player-auth', payload);
  }

  // Submit a parent access request from the public landing page. No session
  // involved - the function inserts via service role and returns the code.
  async function submitAccessRequest({ childName, childDob, parentName, parentEmail, website }) {
    return _callPlayerAuth({
      action: 'request',
      childName, childDob, parentName, parentEmail, website,
    });
  }

  // Submit a coach/trainer access request from the public apply screen. The
  // staff-auth function inserts a pending request (service role) and returns an
  // ST- code that stays inactive until the director approves.
  async function submitStaffRequest({ applicantName, contact, teamNote, website }) {
    return _callFunction('staff-auth', {
      action: 'request',
      applicantName, contact, teamNote, website,
    });
  }

  async function _callFunction(name, payload) {
    const url = `${CONFIG.supabase.url}/functions/v1/${name}`;
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey':        CONFIG.supabase.anonKey,
          'Authorization': `Bearer ${CONFIG.supabase.anonKey}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify(payload),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: body.error || `http_${r.status}` };
      return { ok: true, ...body };
    } catch {
      return { ok: false, error: 'network' };
    }
  }

  // ── SESSION MANAGEMENT ───────────────────────────────────────────────────────

  // Create a director session.
  function setDirectorSession() {
    // The pending exchange holds the JWT we got from the Edge Function.
    if (_pendingExchange) {
      _session = { ..._pendingExchange.session, token: _pendingExchange.token };
      _pendingExchange = null;
      _persist();
    }
  }

  // Create a coach session after name entry.
  async function setCoachSession(coachName, codeRecord) {
    const code = _pendingCode || codeRecord?.code;
    if (!code) return { ok: false, error: 'missing_code' };
    const r = await _exchange(code, coachName.trim());
    if (!r.ok) return { ok: false, error: r.error };
    _session = { ...r.session, token: r.token };
    _pendingCode = null;
    _persist();
    return { ok: true };
  }

  // Current session JWT - sent in Authorization on every DB request.
  function getToken() {
    return _session?.token || null;
  }

  // Clear the current session (logout).
  function clearSession() {
    _session = null;
    sessionStorage.removeItem(SESSION_KEY);
  }

  // ── ACCESSORS ────────────────────────────────────────────────────────────────

  const isLoggedIn    = () => !!_session;
  // Held between the two developer steps: the code is NOT persisted, so
  // closing the tab mid-choice leaves nothing behind.
  let _pendingDeveloper = null;

  /**
   * Step two: enter one club as a developer.
   *
   * The server writes the access log BEFORE it mints anything, so a session
   * that exists is a session that was recorded. `why` is free text that lands
   * in that log line, which is the difference between a trace and a tally, and
   * it is REQUIRED: too short and the function answers 'missing_reason' and
   * nothing is logged, entered or minted.
   */
  async function enterClubAsDeveloper(clubId, why) {
    if (!_pendingDeveloper) return { valid: false, reason: 'no_developer_code' };
    const r = await _callFunction('developer-auth', {
      code: _pendingDeveloper.code, club: clubId, why: why || '',
    });
    if (!r.ok) return { valid: false, reason: r.error };
    _session = { ...r.session, token: r.token };
    _pendingDeveloper = null;
    _persist();
    return { valid: true, isDeveloper: true };
  }

  /** Is this session support access rather than the club's own director? */
  const isDeveloper   = () => !!_session?.isDeveloper;

  const isDirector    = () => _session?.mode === 'director';
  const isCoDirector  = () => _session?.mode === 'codirector';
  const isCoach       = () => _session?.mode === 'coach';
  const isFeedback    = () => _session?.mode === 'feedback';
  const isSelection   = () => _session?.mode === 'selection';
  const isPlayer      = () => _session?.mode === 'player';
  const getPlayerId   = () => _session?.playerId   || null;
  /** The whole session, for screens that need more than one field of it. */
  const getSession    = () => _session;
  const isSquad       = () => _session?.mode === 'squad';
  const getPlayerName = () => _session?.playerName || '';
  const isStaff       = () => _session?.mode === 'staff';
  const getStaffId    = () => _session?.staffId    || null;
  const getStaffName  = () => _session?.staffName  || '';
  const isStaffEvaluator = () => !!_session?.isEvaluator;
  /** Head trainers and directors: no fortnightly pillar rotation. */
  const isUnrestrictedEvaluator = () => !!_session?.unrestrictedEval;
  const isStaffTrainer   = () => !!_session?.isTrainer;
  /**
   * May read the season plan. Granted per person by the director rather than by
   * role, so a coach and a trainer can each have it or not. The directors always
   * do: RLS gives them the whole table regardless, and hiding the way in would
   * only stop them reaching content they own.
   *
   * This mirrors the database rule but does not enforce it. RLS is the fence;
   * this is what stops the app offering a door that opens onto nothing.
   */
  /** The DTA badge this coach wears in their hub header. Null = none. */
  const getDtaBadge = () => _session?.dtaBadge || null;

  const canSeeSeasonPlans = () =>
    isDirector() || isCoDirector() || !!_session?.seesPlans;
  /**
   * Head trainers who read every evaluation in the age groups they oversee.
   *
   * Directors are excluded on purpose: they already read everything and have
   * their own screens for it, so showing them a Coach Hub tab scoped to
   * "their" ages would be a narrower view of what they already have.
   */
  const overseesAges = () => !!_session?.overseesAges;
  const getTrainerAges   = () => _session?.trainerAges || [];
  const isBaselineTester    = () => _session?.mode === 'baseline_tester';
  const getBaselineSessionId   = () => _session?.baselineSessionId   || null;
  const getBaselineSessionName = () => _session?.baselineSessionName || '';
  const getBaselineTesterCodeId = () => _session?.testerCodeId || null;
  const getTesterName          = () => _session?.testerName   || '';
  // Teams this staff coach may evaluate with their personal code, plus the
  // plan that drives their pillar rotation. [{id, age_group, line, plan}].
  const getCoachTeams = () => _session?.coachTeams || [];

  // Enter the player-evaluation context for one of the coach's teams: scope
  // the session to that team and the single pillar open this fortnight, so the
  // existing player-list + rating screens "just work" under the staff role.
  function enterTeamEvalContext(team, pillars) {
    if (!_session) return;
    _session.team    = team ? { id: team.id, age_group: team.age_group, line: team.line } : null;
    _session.pillars = pillars || null;
    _persist();
  }
  const getSelectionRole       = () => _session?.selectionRole       || null;
  const getSelectionTrainerId  = () => _session?.selectionTrainerId  || null;
  const getSelectionTrainingId = () => _session?.selectionTrainingId || null;
  const getSelectionTrainingName = () => _session?.selectionTrainingName || null;
  const getSelectionViewOnly   = () => !!_session?.selectionViewOnly;

  // Record which selection session the trainer has picked.
  function setSelectionTraining(id, name) {
    if (!_session) return;
    _session.selectionTrainingId   = id   || null;
    _session.selectionTrainingName = name || null;
    _persist();
  }
  const getCoachName  = () => _session?.coachName || '';
  const getCode       = () => _session?.code || '';
  const getTeam       = () => _session?.team || null;
  const getPillars    = () => _session?.pillars || null;   // null = all pillars

  /** This viewer's identity applied to the pure test above. */
  const ownsEvaluation = (rec) =>
    isOwnEvaluation(rec, { staffId: getStaffId(), code: getCode() });

  // Check if a specific pillar should be shown for this session.
  function canSeePillar(domain) {
    if (!_session) return false;
    if (isDirector()) return true;
    if (!_session.pillars) return true;   // null = all pillars
    return _session.pillars.includes(domain);
  }

  // Check if this session can edit a given evaluation.
  function canEditEvaluation(evalCode) {
    if (isDirector()) return true;
    // The co-director can edit only her own (CODIRECTOR-stamped) evaluations.
    if (isCoDirector()) return evalCode === CONFIG.access.coDirectorCode;
    return getCode() === evalCode;
  }

  // ── HELPERS ──────────────────────────────────────────────────────────────────

  function _persist() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(_session));
    } catch {
      // sessionStorage not available - session will not persist across refreshes
    }
  }

  // ── PUBLIC API ────────────────────────────────────────────────────────────────

  return {
    init,
    validateCode,
    submitAccessRequest,
    submitStaffRequest,
    setDirectorSession,
    setCoachSession,
    clearSession,
    isPlayer,
    getPlayerId,
    getSession,
    isSquad,
    getPlayerName,
    isStaff,
    getStaffId,
    ownsEvaluation,
    getStaffName,
    isStaffEvaluator,
    isUnrestrictedEvaluator,
    canSeeSeasonPlans,
    getDtaBadge,
    overseesAges,
    isStaffTrainer,
    getTrainerAges,
    getCoachTeams,
    enterTeamEvalContext,
    isBaselineTester,
    getBaselineSessionId,
    getBaselineSessionName,
    getBaselineTesterCodeId,
    getTesterName,
    isLoggedIn,
    isDirector,
    isDeveloper,
    enterClubAsDeveloper,
    isCoDirector,
    isCoach,
    isFeedback,
    getCoachName,
    getCode,
    getTeam,
    getPillars,
    canSeePillar,
    canEditEvaluation,
    getToken,
    isSelection,
    getSelectionRole,
    getSelectionTrainerId,
    getSelectionTrainingId,
    getSelectionTrainingName,
    getSelectionViewOnly,
    setSelectionTraining,
  };

})();
